import React, { useState, useCallback, useEffect, useRef } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

type SearchBarProps = {
  value: string;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  size?: 'default' | 'compact';
};

export const SearchBar = ({ value, onChange, fullWidth, size = 'default' }: SearchBarProps) => {
  const [localValue, setLocalValue] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  const debouncedValue = useDebouncedValue(localValue);
  const skipDebounceRef = useRef(false);

  // Sync local value with external value (for reset filters / navigation)
  if (value !== prevValue) {
    setPrevValue(value);
    setLocalValue(value);
  }

  // Mark that we should skip the next debounce propagation after a parent sync
  useEffect(() => {
    skipDebounceRef.current = true;
  }, [value]);

  // Propagate debounced value to parent
  useEffect(() => {
    if (skipDebounceRef.current) {
      skipDebounceRef.current = false;
      return;
    }
    if (debouncedValue !== value) {
      onChange(debouncedValue);
    }
  }, [debouncedValue, value, onChange]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(event.target.value);
  }, []);

  const handleClear = useCallback(() => {
    skipDebounceRef.current = true;
    setLocalValue('');
    onChange('');
  }, [onChange]);

  return (
    <TextField
      value={localValue}
      onChange={handleChange}
      placeholder="Search..."
      size="small"
      fullWidth={fullWidth}
      sx={{
        ...(!fullWidth && { width: 150 }),
        '& .MuiOutlinedInput-root': {
          height: size === 'compact' ? 32 : 38.5,
          paddingLeft: '8px',
        },
        '& .MuiOutlinedInput-input': {
          paddingLeft: 0,
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: localValue ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleClear} edge="end">
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
};
